// Angular
import { Injectable } from '@angular/core';
// RxJS
import { catchError, map, mergeMap, takeUntil, finalize, repeat } from 'rxjs/operators';
import { forkJoin, Observable, of, throwError } from 'rxjs';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
// CRUD
import {
    LayoutUtilsService,
    MessageType,
    QueryParamsModel,
    QueryResultsModel,
} from '../../_base/crud';
// Services
import { AuthService } from '../../../core/auth/_services';
// State
import { AppState } from '../../../core/reducers';
import {
    ActivateUser,
    ActivateUserSuccess,
    BlockUser,
    BlockUserSuccess,
    CreateUser,
    CreateUserFail,
    CreateUserSuccess,
    UserActionTypes,
    UsersActionToggleLoading,
    UsersPageLoaded,
    UsersPageRequested,
    CRMUsersPageRequested,
    UsersPageToggleLoading,
    UserUpdated,
} from '../_actions/user.actions';
import { Update } from '@ngrx/entity';
import { User } from '..';

@Injectable()
export class UserEffects {
    showPageLoadingDistpatcher = new UsersPageToggleLoading({
        isLoading: true,
    });
    hidePageLoadingDistpatcher = new UsersPageToggleLoading({
        isLoading: false,
    });

    showActionLoadingDistpatcher = new UsersActionToggleLoading({
        isLoading: true,
    });
    hideActionLoadingDistpatcher = new UsersActionToggleLoading({
        isLoading: false,
    });

    @Effect()
    loadUsersPage$ = this.actions$.pipe(
        ofType<UsersPageRequested>(UserActionTypes.UsersPageRequested),
        mergeMap(({ payload }) => {
            this.store.dispatch(this.showPageLoadingDistpatcher);
            const requestToServer = this.auth.findUsers(payload.page);
            const lastQuery = of(payload.page);
            return forkJoin(requestToServer, lastQuery);
        }),
        map((response) => {
            const result: QueryResultsModel = response[0];
            const lastQuery: QueryParamsModel = response[1];
            return new UsersPageLoaded({
                users: result.items,
                totalCount: result.totalCount,
                page: lastQuery,
            });
        }),
        takeUntil(this.actions$.pipe(
            ofType(UserActionTypes.CANCEL_SEARCH_REQUEST)          
        )),
        finalize(() => this.store.dispatch(this.hidePageLoadingDistpatcher)),
        repeat()
    );

    @Effect()
    loadCRMUsersPage$ = this.actions$.pipe(
        ofType<CRMUsersPageRequested>(UserActionTypes.CRMUsersPageRequested),
        mergeMap(({ payload }) => {
            this.store.dispatch(this.showPageLoadingDistpatcher);
            const requestToServer = this.auth.findCRMUsers(payload.page);
            const lastQuery = of(payload.page);
            return forkJoin(requestToServer, lastQuery);
        }),
        map((response) => {
            const result: QueryResultsModel = response[0];
            const lastQuery: QueryParamsModel = response[1];
            return new UsersPageLoaded({
                users: result.items,
                totalCount: result.totalCount,
                page: lastQuery,
            });
        })
    );

    @Effect()
    deactivateUser$: Observable<Action> = this.actions$.pipe(
        ofType<BlockUser>(UserActionTypes.BlockUser),
        mergeMap(({ payload: { user } }) => {
            return this.auth.blockUser(user.id).pipe(
                map((res) => {
                    const userUpdated: Update<User> = {
                        id: res.id,
                        changes: res,
                    };
                    return new BlockUserSuccess({ userUpdated });
                })
            );
        })
    );

    @Effect({ dispatch: false })
    deactivateUserSuccess$ = this.actions$.pipe(
        ofType<BlockUserSuccess>(UserActionTypes.BlockUserSuccess),
        map(({ payload }) => {
            this.layoutUtilsService.showActionNotification(
                'El usuario ha sido bloqueado',
                MessageType.Update,
                5000,
                true,
                false
            );
        })
    );

    @Effect()
    activateUser$: Observable<Action> = this.actions$.pipe(
        ofType<ActivateUser>(UserActionTypes.ActivateUser),
        mergeMap(({ payload: { user } }) => {
            return this.auth.activateuser(user.id).pipe(
                map((res) => {
                    const userUpdated: Update<User> = {
                        id: res.id,
                        changes: res,
                    };
                    return new ActivateUserSuccess({ userUpdated });
                })
            );
        })
    );

    @Effect({ dispatch: false })
    activateUserSuccess$ = this.actions$.pipe(
        ofType<ActivateUserSuccess>(UserActionTypes.ActivateUserSuccess),
        map(({ payload }) => {
            this.layoutUtilsService.showActionNotification(
                'El usuario ha sido activado',
                MessageType.Update,
                5000,
                true,
                false
            );
        })
    );

    @Effect()
    updateUser$ = this.actions$.pipe(
        ofType<UserUpdated>(UserActionTypes.UserUpdated),
        mergeMap(({ payload }) => {
            this.store.dispatch(this.showActionLoadingDistpatcher);
            return this.auth.updateUser(payload.user).pipe(
                map((res) => {
                    this.layoutUtilsService.showActionNotification(
                        'Usuario guardado exitosamente.',
                        MessageType.Update,
                        5000,
                        true,
                        false
                    );
                }),
                catchError((error) => {
                    this.layoutUtilsService.showActionNotification(
                        error.error.message,
                        MessageType.Update,
                        5000,
                        true,
                        false
                    );
                    return throwError(error)
                })
            );
        }),
        map(() => {
            return this.hideActionLoadingDistpatcher;
        })
    );

    @Effect()
    createUser$ = this.actions$.pipe(
        ofType<CreateUser>(UserActionTypes.CreateUser),
        mergeMap(({ payload: { user } }) => {
            return this.auth.createUser(user).pipe(
                map((res) => new CreateUserSuccess({ user: res })),
                catchError((err) => of(new CreateUserFail(err)))
            );
        })
    );

    constructor(
        private actions$: Actions,
        private auth: AuthService,
        private store: Store<AppState>,
        private layoutUtilsService: LayoutUtilsService
    ) {}
}
